import SoftBadge from 'components/common/SoftBadge';
import TableRowAction from 'components/common/TableRowAction';
import CustomAdvanceTable from 'components/common/advance-table/CustomAdvanceTable';
import { Col, Row, Button, Modal, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import ExportModal from 'components/common/ExportModal';
import ConfirmationModal from 'components/common/modals/ConfirmationModal';
import AuthContext, { rolesMap } from 'context/authContext';
import { parseRouteParams } from 'helpers/utils';
import { useContext, useEffect, useState } from 'react';
import paths from 'routes/paths';
import { permissionsKeyMap } from 'routes/permissions/permissionKeys';
import { useBranches } from 'hooks/useBranches';
import {
  useUserRoles,
  useBranchUserRoles,
  useDeleteUserRole
} from 'hooks/useUsers';

const UsersList = () => {
  const navigate = useNavigate();
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedFilterBranch, setSelectedFilterBranch] = useState('');
  const [appliedFilterBranch, setAppliedFilterBranch] = useState('');
  const columns = [
    {
      accessor: 'name',
      Header: 'Name',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { name } = rowData.row.original;
        return name;
        // <Flex alignItems="center">
        //   <Link
        //     to={`/donors/${id}`}
        //     className="fw-medium ms-2 text-1100 hover-primary"
        //   >
        //     {name}
        //   </Link>
        // </Flex>
      }
    },
    {
      accessor: 'email',
      Header: 'Email',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { email } = rowData.row.original;
        return (
          <a className="fw-medium text-600" href={'mailto:' + email}>
            {email}
          </a>
        );
      }
    },
    {
      accessor: 'branch',
      Header: 'Branch',
      cellProps: {
        className: 'fw-medium text-600 py-2'
      }
    },
    {
      accessor: 'role',
      Header: 'Role',
      cellProps: {
        className: 'fw-medium text-600 py-2'
      }
    },
    {
      accessor: 'status',
      Header: 'Status',
      cellProps: {
        className: 'fw-medium py-2'
      },

      Cell: rowData => {
        const { status: isActive } = rowData.row.original;
        return (
          <SoftBadge pill bg={isActive ? 'success' : 'danger'}>
            {isActive ? 'Active' : 'Inactive'}
          </SoftBadge>
        );
      }
    },
    {
      accessor: 'id',
      Header: '',
      cellProps: {
        className: 'py-2 text-end'
      },
      disableSortBy: true,
      Cell: rowData => {
        const { id, isOrganizationAdmin: isUserOrgAdmin } =
          rowData.row.original;
        if (isUserOrgAdmin) return null;
        return (
          <TableRowAction
            onCLickEdit={() =>
              navigate(parseRouteParams(paths.editUser, { id }))
            }
            onCLickDelete={() => handleDeleteClick(id)}
            subject={permissionsKeyMap.settings.children.users}
          />
        );
      }
    }
  ];
  const [selectedId, setSelectedId] = useState(null);
  const { organization, selectedBranchId, isOrganizationAdmin } =
    useContext(AuthContext);
  const [userTableData, setUserTableData] = useState([]);
  const [showExportModal, setShowExportModal] = useState(false);

  // Use Supabase hooks based on user role
  const isOrgAdmin = isOrganizationAdmin;

  // Get user roles - use appropriate hook based on role
  const { userRoles: allUserRoles, isLoading: isLoadingAllUsers } =
    useUserRoles();
  const { userRoles: branchUserRoles, isLoading: isLoadingBranchUsers } =
    useBranchUserRoles(!isOrgAdmin ? selectedBranchId : null);

  // Select the appropriate data based on role
  const userRoles = isOrgAdmin ? allUserRoles : branchUserRoles;
  const isLoading = isOrgAdmin ? isLoadingAllUsers : isLoadingBranchUsers;

  // Get branches for filter (only for org admins)
  const { branches } = useBranches();

  // Get delete mutation
  const { deleteUserRoleAsync, isDeleting } = useDeleteUserRole();

  useEffect(() => {
    let filteredData = userRoles || [];

    // Apply branch filter if set
    if (isOrgAdmin && appliedFilterBranch) {
      filteredData = filteredData.filter(
        item => item.branch_id === appliedFilterBranch
      );
    }

    const tmp = filteredData.map(item => {
      return {
        id: item.id,
        name: item.user.first_name + ' ' + item.user.last_name,
        email: item.user.email,
        branch: item.branch?.name,
        branch_id: item.branch_id,
        isOrganizationAdmin: item.role_id === rolesMap.organisationAdmin.id,
        isBranchAdmin: item.role_id === rolesMap.admin.id,
        role: item.role.name,
        status: item.is_active
      };
    });
    setUserTableData(tmp);
  }, [userRoles, appliedFilterBranch, isOrgAdmin]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDeleteClick = id => {
    setSelectedId(id);
    setShowDeleteModal(true);
  };
  const handleDeleteModalHide = () => {
    setSelectedId(null);
    setShowDeleteModal(false);
  };
  const handleConfirmDelete = async () => {
    try {
      await deleteUserRoleAsync(selectedId);
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Delete error:', error);
      // Error toast is handled in the hook
    }
  };
  const handleExport = () => {
    setShowExportModal(true);
  };

  const handleFilter = () => {
    setShowFilterModal(true);
  };

  const handleApplyFilter = () => {
    setAppliedFilterBranch(selectedFilterBranch);
    setShowFilterModal(false);
  };

  const handleClearFilter = () => {
    setSelectedFilterBranch('');
    setAppliedFilterBranch('');
    setShowFilterModal(false);
  };

  return (
    <>
      <ConfirmationModal
        show={showDeleteModal}
        onHide={handleDeleteModalHide}
        loading={isDeleting}
        title="Delete User"
        handleConfirm={handleConfirmDelete}
        body="Are you sure you want to delete selected user?"
      />
      <Row>
        <Col md={12} xxl={12} className="h-md-100">
          {userTableData && (
            <CustomAdvanceTable
              data={userTableData || []}
              columns={columns}
              title="User list"
              addNew
              hasFilters={isOrgAdmin}
              hasExport
              handleExport={handleExport}
              handleFilter={handleFilter}
              addButtonLabel="New"
              showSearchInput
              searchInputPlaceHolder="Search Users"
              onClickAddNew={() => navigate(paths.createNewUser)}
              subject={permissionsKeyMap.settings.children.users}
            />
          )}
        </Col>
      </Row>
      <ExportModal
        data={userTableData || []}
        columns={columns.slice(0, columns.length - 1)}
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        fileName={'User List'}
      />

      {/* Filter Modal */}
      <Modal show={showFilterModal} onHide={() => setShowFilterModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Filter Users by Branch</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Select Branch</Form.Label>
            <Form.Select
              value={selectedFilterBranch}
              onChange={e => setSelectedFilterBranch(e.target.value)}
            >
              <option value="">All Branches</option>
              {branches?.map(branch => (
                <option key={branch.id} value={branch.id}>
                  {branch.name} {branch.is_main ? '(Main)' : ''}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          {appliedFilterBranch && (
            <div className="mt-3 text-muted">
              <small>
                Current filter:{' '}
                {branches?.find(b => b.id === appliedFilterBranch)?.name}
              </small>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClearFilter}>
            Clear Filter
          </Button>
          <Button variant="primary" onClick={handleApplyFilter}>
            Apply Filter
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UsersList;
